import React, { useState, useEffect, useMemo } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png";

function ClassSection() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");

  const [triggerRerender, setTriggerRerender] = useState(false);

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      id: 0,
      name: "",
      centerClassId: "",
      centerId: "",
      comments: "",
      userId: localStorage.getItem("userId"),
    },
  });

  const [selectedCenterId, setSelectedCenterId] = useState(
    savedCenterId || null
  );
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [sections, setSections] = useState([]);
  const [centersData, setCentersData] = useState([]);
  const [classesData, setClassesData] = useState([]);
  const [availabilityMessage, setAvailabilityMessage] = useState("");
  const [availabilityColor, setAvailabilityColor] = useState("");

  useEffect(() => {
    const fetchCenters = async () => {
      try {
        const response = await get(
          `Dropdown/GetAllCentersByOrganizationId?organizationId=${organizationId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setCentersData(response);
        if (savedCenterId) {
          const selectedCenter = response.find(
            (center) => center.id === parseInt(savedCenterId, 10)
          );
          if (selectedCenter) {
            setValue("centerId", {
              value: selectedCenter.id,
              label: selectedCenter.name,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching centers:", error);
      }
    };

    fetchCenters();
  }, [setValue, triggerRerender, savedCenterId]);

  const fetchClassesByCenterId = async (centerId) => {
    try {
      if (centerId) {
        const response = await get(
          `CenterClass/GetClassCenterByCenterId?id=${centerId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setClassesData(response);
      } else {
        setClassesData([]);
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  useEffect(() => {
    if (selectedCenterId) {
      fetchClassesByCenterId(selectedCenterId);
    }
  }, [selectedCenterId]);

  const getAllClassSectionsByClassIdAndCenterId = async (classId, centerId) => {
    try {
      if (classId && centerId) {
        const response = await get(
          `ClassSection/GetAllSectionByCenterClassIdAndCenterId?centerClassId=${classId}&centerId=${centerId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setSections(response);
      } else {
        setSections([]);
      }
    } catch (error) {
      console.error("Error fetching sections:", error);
    }
  };

  useEffect(() => {
    if (selectedCenterId && selectedClassId) {
      getAllClassSectionsByClassIdAndCenterId(
        selectedClassId,
        selectedCenterId
      );
    }
  }, [selectedCenterId, selectedClassId, triggerRerender]);

  const checkSectionAvailability = async () => {
    const centerId = getValues("centerId")?.value;
    const classId = getValues("centerClassId")?.value;
    const sectionName = getValues("name");

    if (!centerId || !classId || !sectionName) {
      setAvailabilityMessage("");
      trigger("centerId");
      trigger("centerClassId");
      trigger("name");
      return;
    }

    trigger("name");

    try {
      const response = await get(
        `ClassSection/SectionAvailability?id=${classId}&&sectionName=${sectionName}&&centerId=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response) {
        setAvailabilityMessage("Not Available");
        setAvailabilityColor("text-danger");
      } else {
        setAvailabilityMessage("Available");
        setAvailabilityColor("text-success");
      }
    } catch (error) {
      console.error("Error checking availability:", error);
    }
  };

  const handleEdit = async (row) => {
    try {
      const response = await get(
        `ClassSection/GetClassSectionById?id=${row.id}`,

        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const singleSectionData = response.section;

      const selectedCenter = centersData.find(
        (center) => center.id === singleSectionData.centerId
      );

      const selectedClass = classesData.find(
        (classItem) => classItem.id === singleSectionData.centerClassId
      );

      reset({
        id: singleSectionData.id || "",
        centerId: {
          value: selectedCenter?.id || "",
          label: selectedCenter?.name || "",
        },
        centerClassId: {
          value: selectedClass?.id || "",
          label: selectedClass?.name || "",
        },
        name: singleSectionData.name || "",
        comments: singleSectionData.comments || "",
        userId: singleSectionData.userId,
      });
    } catch (error) {
      console.error("Error editing section:", error);
    }
  };

  const handleDelete = async (row) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this section?"
    );

    if (!confirmed) return;

    try {
      const response = await get(
        `ClassSection/DeleteClassSection?id=${row.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.success) {
        toast.success(response.message || "Success!");
        setTriggerRerender((prev) => !prev);
      } else {
        toast.error(response.message || "An error occurred while deleting.");
      }
    } catch (error) {
      console.error("Error deleting section:", error);
    }
  };

  const columns = React.useMemo(
    () => [
      {
        accessorKey: "mrt-row-numbers",
        header: "Sr No.",
        enableColumnOrdering: false,
        enableEditing: false,
        enableSorting: false,
        size: 100,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "name",
        header: "Name",
        size: 150,
      },
      {
        accessorKey: "comments",
        header: "Description",
        size: 250,
      },
      {
        accessorKey: "actions",
        header: "Action",
        size: 120,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <div className="d-flex gap-1">
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleEdit(row.original)}
            >
              <i className="fas fa-pencil"></i>
            </button>
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleDelete(row.original)}
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    [centersData, classesData]
  );

  const exportToExcel = () => {
    const filteredData = sections.map((row, index) => ({
      "Sr No": index + 1,
      Name: row.name,
      Description: row.comments,
    }));

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sections");
    XLSX.writeFile(workbook, "Sections.xlsx");
  };

  const loadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = url;
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
    });
  };

  const exportToPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;

    const roleId = localStorage.getItem("roleId");
    const orgLogoPath = localStorage.getItem("organizationLogoPath");
    const centerLogoPath = localStorage.getItem("centerLogoPath");
    const orgLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${orgLogoPath}`;
    const centerLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${centerLogoPath}`;

    let logoToUse = logo; // Default imported logo
    // Logic to determine which logo to use
    // If roleId is 2 (Organization), use orgLogo
    // If roleId is 3 (Center), use centerLogo
    const sidebarLogoUrl =
      roleId == "2" ? orgLogo : roleId == "3" ? centerLogo : null;

    if (sidebarLogoUrl) {
      try {
        logoToUse = await loadImage(sidebarLogoUrl);
      } catch (error) {
        console.warn(
          "Failed to load sidebar logo, falling back to default.",
          error
        );
      }
    }

    // Add only the resolved logo
    // doc.addImage(logoToUse, "PNG", logoX, logoY, logoWidth, logoHeight);

    const title = "Sections";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const includedColumns = columns.filter(
      (column) =>
        column.accessorKey !== "actions" &&
        column.accessorKey !== "mrt-row-numbers"
    );

    const headers = ["Sr No.", ...includedColumns.map((col) => col.header)];

    const bodyData = sections.map((row, index) => {
      return [
        index + 1,
        ...includedColumns.map((column) => row[column.accessorKey] || ""),
      ];
    });

    doc.autoTable({
      head: [headers],
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });

    doc.save("SectionsList.pdf");
  };

  useEffect(() => {
    const tooltipTriggerList = Array.from(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );
    tooltipTriggerList.forEach((tooltipTriggerEl) => {
      new window.bootstrap.Tooltip(tooltipTriggerEl);
    });
  }, []);

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    const formData = {
      ...data,
      centerId: data.centerId.value,
      centerClassId: data.centerClassId.value,
    };

    try {
      const response = await post("ClassSection/SaveClassSection", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.success) {
        toast.success(response.message || "Success!");
        setTriggerRerender((prev) => !prev);

        const { centerId, centerClassId } = data;

        reset({
          id: 0,
          centerClassId,
          centerId,
          name: "",
          comments: "",
          userId: localStorage.getItem("userId"),
        });
        setAvailabilityMessage("");
      }
    } catch (error) {
      console.error("Error.", error);
    }
  };

  const handleChangeOfInputWithLengthCheck = (fieldName) => (e) => {
    const value = e.target.value;
    setValue(fieldName, value, { shouldValidate: true });
  };

  return (
    <div className="">
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Class Sections</h2>
      </div>

      <div className="row g-3">
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <form
                className="row g-3 new-form-style"
                onSubmit={handleSubmit(saveData)}
              >
                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    Center<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="centerId"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={centersData.map((center) => ({
                          value: center.id,
                          label: center.name,
                        }))}
                        // isDisabled={!!savedCenterId}
                        isDisabled={savedCenterId !== "0"}
                        onChange={(selectedOption, actionMeta) => {
                          if (actionMeta.action === "clear") {
                            setValue("centerId", null);
                            setValue("centerClassId", null);
                            setClassesData([]);
                            setSelectedCenterId(null);
                            setSelectedClassId(null);
                            setSections([]); // Clear sections as well
                          } else {
                            setValue("centerId", selectedOption);
                            setValue("centerClassId", null);
                            setSelectedCenterId(selectedOption.value);
                            setSelectedClassId(null);
                            fetchClassesByCenterId(selectedOption.value);
                          }
                          trigger("centerId");
                        }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        isClearable={
                          savedCenterId !== null && savedCenterId !== 0
                        }
                      />
                    )}
                  />
                  {errors.centerId && (
                    <small className="text-danger">
                      {errors.centerId.message}
                    </small>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    Class<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="centerClassId"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={classesData.map((classItem) => ({
                          value: classItem.id,
                          label: classItem.name,
                        }))}
                        onChange={(selectedOption, actionMeta) => {
                          if (actionMeta.action === "clear") {
                            setValue("centerClassId", null);
                            setSelectedClassId(null);
                            setSections([]); // Clear sections if class is cleared
                          } else {
                            setValue("centerClassId", selectedOption);
                            setSelectedClassId(selectedOption.value);
                          }
                          trigger("centerClassId");
                        }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        isClearable
                      />
                    )}
                  />
                  {errors.centerClassId && (
                    <small className="text-danger">
                      {errors.centerClassId.message}
                    </small>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    <div className="d-flex align-items-center">
                      Section Name<span className="text-danger fs-9">*</span>
                    </div>
                  </label>
                  <input
                    {...register(`name`, {
                      required: "This field is required.",
                      maxLength: {
                        value: 75,
                        message: "Maximum of 75 characters allowed.",
                      },
                    })}
                    type="text"
                    className="form-control"
                    placeholder=""
                    onChange={handleChangeOfInputWithLengthCheck("name")}
                  />
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      checkSectionAvailability();
                    }}
                    className="fs-9"
                  >
                    <i className="fa fa-info-circle"></i> Check Availability
                  </a>
                  <div className="d-flex align-items-center mt-1">
                    {errors.name && (
                      <small className="text-danger">
                        {errors.name.message}
                      </small>
                    )}
                    {availabilityMessage && (
                      <small className={`ms-auto ${availabilityColor}`}>
                        {availabilityMessage}
                      </small>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    Description
                  </label>
                  <input
                    {...register(`comments`, {
                      maxLength: {
                        value: 250,
                        message: "Maximum of 250 characters allowed.",
                      },
                    })}
                    type="text"
                    className="form-control"
                    placeholder=""
                    onChange={handleChangeOfInputWithLengthCheck(`comments`)}
                  />
                  {errors.comments && (
                    <small className="text-danger">
                      {errors.comments.message}
                    </small>
                  )}
                </div>

                <div className="col-12 mt-3">
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Save" : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <CustomDatatableGrid
                columns={columns}
                data={sections}
                onExportExcel={exportToExcel}
                onExportPDF={exportToPDF}
                initialState={{
                  columnPinning: { right: ["actions"] },
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClassSection;
